<template>
  <div class="englobe-params">
    <div class="params-row">
      <GoBackButton :path="backRoute"> Parties Prenantes</GoBackButton>
    </div>
    <div class="params-row">
      <RouterRadioButtons :options="textOptions" />
    </div>

    <div
      v-if="isRoutePublications || isRouteMentions"
      class="stakeholders-zoom-params__filters"
    >
      <div class="params-row">
        <div class="params-row request-and-check-all">
          <SwitchSlide
            :is-active="!storeStakeholder.bypassQuery"
            @toggle="toggleUseRequest"
          >
            Utiliser la requête
          </SwitchSlide>

          <CheckBox
            :checked="isAllSelected"
            @update:checked="
              () => (isAllSelected ? unselectAllFilters() : selectAllFilters())
            "
          >
            Toutes les publications
          </CheckBox>
        </div>

        <div class="params-row">
          <div class="page-settings">
            <DropdownMenu
              v-model="selectedSorterOption"
              :options="sortersOptions"
              label="Trier par :"
            />
            <DropdownMenu
              v-model="selectedLimitOption"
              :options="limitOptions"
              label="Publications par page :"
            />
          </div>
        </div>
      </div>

      <div v-if="isRoutePublications" class="params-row">
        <div class="page-settings filters">
          <FilterCheckBox
            v-for="itemSocialMedia in itemsFiltersSocialMedia"
            :key="itemSocialMedia.label"
            :label="itemSocialMedia.label"
            :color="itemSocialMedia.color"
            :is-checked="
              zoomPublicationsIndexes.includes(itemSocialMedia.indexes)
            "
            @check="addFilter(itemSocialMedia.indexes)"
            @uncheck="removeFilter(itemSocialMedia.indexes)"
          />
        </div>
      </div>
    </div>
    <div v-if="isRouteMentions"></div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import GoBackButton from "@follaw-fr/components/general/atoms/GoBackButton.vue";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import RouterRadioButtons from "@follaw-fr/components/reusable/molecules/RouterRadioButtons.vue";
import FilterCheckBox from "@follaw-fr/components/reusable/atoms/FilterCheckBox.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import { useStakeholdersStore } from "@follaw-fr/stores/social-media/stakeholders";
import { Platforms } from "@follaw-fr/utils/social360-platforms.js";
import side_menu from "@follaw-fr/utils/side_menu";

const storeStakeholder = useStakeholdersStore();
const currentRoute = useRoute();
const {
  zoomPublicationsIndexes,
  zoomPublicationsFilters,
  zoomMentionsFilters,
} = storeToRefs(storeStakeholder);

const isRoutePublications = computed(() => {
  return currentRoute.name === "Zoom Partie Prenante - Publications";
});

const isRouteMentions = computed(() => {
  return currentRoute.name === "Zoom Partie Prenante - Mentions";
});

const currentFilters = computed(() => {
  if (isRoutePublications.value) {
    return zoomPublicationsFilters.value;
  } else if (isRouteMentions.value) {
    return zoomMentionsFilters.value;
  }
  return null;
});

const getPublicationsList = async () => {
  resetPagination();
  if (isRouteMentions.value) {
    await storeStakeholder.getMentionsOfPublications(
      storeStakeholder.authorZoom.x_information?.username
    );
  } else {
    await storeStakeholder.getPublicationsList(currentRoute.params._id);
  }
};

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: {
      like: "Nombre de likes",
      impression: "Nombre d'impressions",
      created_at: "Date",
    },
    selectedFilters: currentFilters,
    filterKey: "sort_field",
    onChange: () => getPublicationsList(),
  });

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: {
      10: "10",
      20: "20",
      50: "50",
      200: "200",
    },
    selectedFilters: currentFilters,
    filterKey: "size",
    onChange: () => {
      currentFilters.value.from = 0;
      getPublicationsList();
    },
  });

const backRoute = "/stakeholders/ranking";
const routerOptions = side_menu
  .find((route) => route.name === "Social Media")
  .secondary_tabs.find((tab) => tab.name === "Parties Prenantes")
  .secondary_routes.find(
    (route) => route.path === "/stakeholders/:_id"
  ).children;

const textOptions = computed(() => {
  const childrenRoutesArray = routerOptions.map((childRoute) => ({
    label: childRoute.name.replace("Zoom Partie Prenante - ", ""),
    value: `/stakeholders/${currentRoute.params._id}/${childRoute.path}`,
  }));

  // Mentions tab won't appear if no mentions
  return childrenRoutesArray.filter((childRoute) =>
    storeStakeholder.zoomMentionsCount > 0
      ? childRoute
      : childRoute.label !== "Mentions"
  );
});

const itemsFiltersSocialMedia = [
  {
    label: "𝕏",
    color: "var(--clr-twitter)",
    indexes: Platforms.TWITTER,
  },
  {
    label: "Linkedin",
    color: "var(--clr-linkedin)",
    indexes: Platforms.LINKEDIN,
  },
  {
    label: "Instagram",
    color: "var(--clr-instagram)",
    indexes: Platforms.INSTAGRAM,
  },
  {
    label: "Tiktok",
    color: "var(--clr-tiktok)",
    indexes: Platforms.TIKTOK,
  },
  {
    label: "Bluesky",
    color: "var(--clr-bluesky)",
    indexes: Platforms.BLUESKY,
  },
];

const resetPagination = () => {
  currentFilters.value.from = 0;
};

const isAllSelected = computed(() => {
  return (
    zoomPublicationsIndexes.value.length === Object.keys(Platforms)?.length
  );
});

const addFilter = (filter) => {
  zoomPublicationsIndexes.value.push(filter);
  getPublicationsList();
};

const removeFilter = (filter) => {
  zoomPublicationsIndexes.value = zoomPublicationsIndexes.value.filter(
    (f) => f !== filter
  );
  getPublicationsList();
};

const unselectAllFilters = () => {
  zoomPublicationsIndexes.value = [];
  getPublicationsList();
};

const selectAllFilters = () => {
  zoomPublicationsIndexes.value = [
    Platforms.TWITTER,
    Platforms.LINKEDIN,
    Platforms.INSTAGRAM,
    Platforms.TIKTOK,
    Platforms.BLUESKY,
  ];
  getPublicationsList();
};

const toggleUseRequest = () => {
  storeStakeholder.bypassQuery = !storeStakeholder.bypassQuery;
  getPublicationsList();
};
</script>

<style scoped lang="scss">
.back {
  width: fit-content;
}

.filters {
  gap: 10px;
}

.filter {
  width: auto;
  gap: 40px;
}

.request-and-check-all {
  justify-content: flex-start;
  gap: 15px;
}

.stakeholders-zoom-params__filters {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
